import React, { useState } from 'react'
import { Budget, BudgetAmountKind, Category } from '@/domain/models'
import { budgetLimit } from '@/domain/usecases'
import { CATEGORY_META } from '@/presentation/constants/category-meta'
import { fmtBRL } from '@/presentation/utils/format'
import { Icon } from '../icon/icon'
import { Button } from '../button/button'
import { Segmented } from '../segmented/segmented'

const ALL_CATEGORIES = Object.values(Category) as Category[]

type BudgetModalProps = {
  budget: Budget | null
  takenCategories: Set<Category>
  monthlyIncome: number
  onClose: () => void
  onSave: (budget: Budget) => Promise<void>
}

export const BudgetModal: React.FC<BudgetModalProps> = ({
  budget, takenCategories, monthlyIncome, onClose, onSave,
}) => {
  const editing = !!budget
  const [name, setName] = useState(budget?.name ?? '')
  const [amountKind, setAmountKind] = useState<BudgetAmountKind>(budget?.amountKind ?? 'percent')
  const [amount, setAmount] = useState(budget ? String(budget.amount) : '')
  const [categories, setCategories] = useState<Category[]>(budget?.categories ?? [])
  const [countsInTotal, setCountsInTotal] = useState(budget?.countsInTotal ?? true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const parsedAmount = parseFloat(amount.replace(',', '.'))
  const validAmount = !isNaN(parsedAmount) && parsedAmount > 0 && (amountKind === 'fixed' || parsedAmount <= 100)
  const draft: Budget = {
    id: budget?.id ?? 0,
    name: name.trim(),
    amountKind,
    amount: validAmount ? parsedAmount : 0,
    categories,
    position: budget?.position ?? 0,
    countsInTotal,
  }
  const preview = budgetLimit(draft, monthlyIncome)

  const toggleCategory = (category: Category) => {
    setCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !validAmount) return

    setSaving(true)
    setError('')
    try {
      await onSave(draft)
    } catch {
      setError('Não foi possível salvar o orçamento.')
      setSaving(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 text-sm border border-border-2 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-green-100 focus:border-green-500'

  return (
    <div
      data-testid="modal-overlay"
      className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4"
      style={{ animation: 'fadeIn 150ms ease' }}
      onMouseDown={onClose}
    >
      <div
        data-testid="budget-modal"
        className="bg-surface rounded-card w-full max-w-[480px] shadow-lg relative"
        style={{ animation: 'modalPop 180ms cubic-bezier(.2,.8,.2,1)' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-[15.5px] font-semibold text-text">
            {editing ? 'Editar orçamento' : 'Novo orçamento'}
          </h2>
          <button data-testid="btn-close" onClick={onClose} className="text-text-3 hover:text-text-4 p-1">
            <Icon name="x" size={20} />
          </button>
        </div>

        <form aria-label="form" role="form" onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <label className="block">
            <span className="text-xs font-medium text-text-2 mb-1 block">Nome</span>
            <input
              data-testid="input-name"
              type="text" value={name} maxLength={60} required
              placeholder="Ex.: Despesas, Gastos livres…"
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
            />
          </label>

          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-text-2">Tipo de limite</span>
            <Segmented
              options={[
                { value: 'percent', label: '% da renda' },
                { value: 'fixed', label: 'Valor fixo' },
              ]}
              value={amountKind}
              onChange={setAmountKind}
            />
          </div>

          <label className="block">
            <span className="text-xs font-medium text-text-2 mb-1 block">
              {amountKind === 'percent' ? 'Percentual da renda do mês' : 'Valor mensal'}
            </span>
            <div className="flex items-center border border-border-2 rounded-[8px] focus-within:ring-2 focus-within:ring-green-100 focus-within:border-green-500">
              <span className="px-3 text-sm text-text-3 border-r border-border-2">
                {amountKind === 'percent' ? '%' : 'R$'}
              </span>
              <input
                data-testid="input-amount"
                type="number" step="0.01" inputMode="decimal" required
                value={amount} placeholder={amountKind === 'percent' ? '50' : '0,00'}
                onChange={(e) => setAmount(e.target.value)}
                className="flex-1 px-3 py-2 text-sm bg-transparent focus:outline-none rounded-r-[8px]"
              />
            </div>
            {amountKind === 'percent' && (
              <span data-testid="amount-preview" className="text-xs text-text-3 mt-1 block">
                {monthlyIncome > 0
                  ? `${fmtBRL(preview)} com a renda deste mês`
                  : 'Sem renda lançada neste mês'}
              </span>
            )}
          </label>

          <div>
            <span className="text-xs font-medium text-text-2 mb-2 block">Categorias</span>
            <div className="flex flex-wrap gap-2">
              {ALL_CATEGORIES.map((category) => {
                const meta = CATEGORY_META[category]
                const selected = categories.includes(category)
                const taken = takenCategories.has(category) && !selected

                return (
                  <button
                    key={category}
                    type="button"
                    data-testid={`cat-${category}`}
                    disabled={taken}
                    onClick={() => toggleCategory(category)}
                    title={taken ? 'Já usada em outro orçamento' : undefined}
                    className={[
                      'flex items-center gap-2 px-3 py-1.5 rounded-pill border text-sm transition-colors',
                      taken ? 'opacity-40 cursor-not-allowed border-border' : 'hover:border-border-2',
                      selected ? 'border-transparent font-medium' : 'border-border text-text-2',
                    ].join(' ')}
                    style={selected ? { background: meta.color + '1f', color: meta.color } : undefined}
                  >
                    <Icon name={meta.icon} size={14} strokeWidth={2} />
                    {meta.label}
                    {selected && <Icon name="check" size={13} strokeWidth={2.4} />}
                  </button>
                )
              })}
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              data-testid="input-counts-in-total"
              type="checkbox"
              checked={!countsInTotal}
              onChange={(e) => setCountsInTotal(!e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-green-600 cursor-pointer"
            />
            <span className="text-xs text-text-2">
              Não considerar na soma de orçamentos
              <span className="block text-text-3">
                O que for gasto aqui não entra no total do mês, como investimentos.
              </span>
            </span>
          </label>

          {error && <p data-testid="modal-error" className="text-xs text-red-500">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving || !name.trim() || !validAmount}>
              {editing ? 'Salvar' : 'Criar orçamento'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
